/**
 * Auth Store
 * Zustand store with localStorage persistence for JWT auth
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api, apiPost } from '@/lib/api';

// ============================================================================
// TYPES
// ============================================================================

export type UserTier = 'free' | 'pro' | 'lifetime';

export interface AuthUser {
  readonly id: string;
  readonly email: string;
  readonly tier: UserTier;
  readonly subscriptionExpires: number | null;
}

interface AuthState {
  readonly user: AuthUser | null;
  readonly token: string | null;
  readonly isLoading: boolean;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearAuth: () => void;
}

interface LoginResponse {
  token: string;
  user: AuthUser;
}

// /auth/me returns the user object directly (not wrapped in { user })
type MeResponse = AuthUser;

// ============================================================================
// STORE
// ============================================================================

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: true,

      login: async (email: string, password: string) => {
        const data = await apiPost<LoginResponse>('/auth/login', { email, password });
        set({ user: data.user, token: data.token, isLoading: false });
      },

      register: async (email: string, password: string) => {
        await apiPost('/auth/register', { email, password });
      },

      logout: async () => {
        try {
          await apiPost('/auth/logout', {});
        } catch {
          // Clear state even if server logout fails
        }
        set({ user: null, token: null, isLoading: false });
      },

      checkAuth: async () => {
        const { token } = get();
        if (!token) {
          set({ isLoading: false });
          return;
        }
        try {
          const data = await api<MeResponse>('/auth/me');
          set({ user: { id: data.id, email: data.email, tier: data.tier, subscriptionExpires: data.subscriptionExpires }, isLoading: false });
        } catch {
          set({ user: null, token: null, isLoading: false });
        }
      },

      clearAuth: () => {
        set({ user: null, token: null, isLoading: false });
      },
    }),
    {
      name: 'pivots-auth',
      partialize: (state) => ({ token: state.token }),
    }
  )
);
